import { Button } from "~/atoms/button";
import { Plus } from "lucide-react";
import { WorkflowBuilderStep } from "~/molecules/WorkflowBuilderStep";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import {
  WorkflowStepStatuses,
  type WorkflowStepInput,
  type WorkflowStepInstance,
} from "~/api/types";
import {
  TimelineStart,
  StepIndicator,
  TimelineEnd,
} from "~/molecules/WorkflowTimelineIndicators";
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { formatDate, dateDiff } from "~/helpers/date";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { isSortableOperation } from "@dnd-kit/react/sortable";

interface Props {
  isEditMode: boolean;
  categoryId: number;
  stepInstances?: WorkflowStepInstance[];
  steps: WorkflowStepInput[];
  setSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  hideDueDates?: boolean;
}

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

export function WorkflowBuilder({
  isEditMode,
  categoryId,
  stepInstances = [],
  steps,
  setSteps,
  setHasUnsavedChanges,
  hideDueDates,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  const nextKeyRef = useRef(0);
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  const handleUpdateStep = useCallback(
    (stepKey: string, updatedStep: WorkflowStepInput) => {
      setSteps((currentSteps) =>
        currentSteps.map((step) =>
          step._key === stepKey ? { ...updatedStep, _key: stepKey } : step,
        ),
      );
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const reorderByIndex = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      setSteps((currentSteps) => {
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= currentSteps.length ||
          toIndex >= currentSteps.length
        ) {
          return currentSteps;
        }

        return moveItemByIndex(currentSteps, fromIndex, toIndex);
      });
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const handleRemoveStep = useCallback(
    (stepKey: string) => {
      setSteps((currentSteps) =>
        currentSteps.filter((step) => step._key !== stepKey),
      );
      setHasUnsavedChanges(true);
    },
    [setHasUnsavedChanges, setSteps],
  );

  const handleAddStep = useCallback(() => {
    setSteps((currentSteps) => [
      ...currentSteps,
      {
        _key: `${id}-${nextKeyRef.current++}`,
        name: `Step ${currentSteps.length + 1}`,
        description: "",
        status: WorkflowStepStatuses.upcoming,
        type: "review",
        order: currentSteps.length,
        dueAt: null,
        users: [],
      },
    ]);
    setHasUnsavedChanges(true);

    // Scroll to the newly added step once it renders
    setTimeout(() => {
      stepsContainerRef.current?.lastElementChild?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  }, [id, setHasUnsavedChanges, setSteps]);

  const stepsWithDueWarning = useMemo(() => {
    const dueWarnings = new Set<number>();
    if (steps.length === 0) return dueWarnings;

    let latestSoFar = formatDate(steps[0]?.dueAt, "date");
    for (let i = 1; i < steps.length; i++) {
      if (!steps[i]?.dueAt) continue;
      const dueAt = formatDate(steps[i]?.dueAt, "date");
      if (dateDiff(dueAt, latestSoFar, "day") < 0) {
        dueWarnings.add(i);
      } else {
        latestSoFar = dueAt;
      }
    }
    return dueWarnings;
  }, [steps]);

  const handleDragEndEvent = useCallback<OnDragEnd>(
    (event) => {
      if (event.canceled) return;
      if (!isSortableOperation(event.operation)) return;

      const source = event.operation.source;
      if (!source) return;

      reorderByIndex(source.initialIndex, source.index);
    },
    [reorderByIndex],
  );

  return (
    <div className="relative space-y-8">
      <div className="bg-primary/40 absolute top-0 bottom-0 left-6 z-0 w-0.5 bg-linear-to-b" />

      <div className="grid grid-cols-[3rem_1fr] gap-x-6">
        <TimelineStart />
      </div>

      <div className="grid grid-cols-[3rem_1fr] gap-x-6 gap-y-8">
        <div
          className="grid grid-rows-subgrid gap-y-8"
          style={{ gridRow: `1 / span ${steps.length || 1}` }}
        >
          {steps.map((step, index) => (
            <div key={step._key} className="z-10 pt-1">
              <StepIndicator value={`${index + 1}`} status={step.status} />
            </div>
          ))}
        </div>
        <div
          className="grid grid-rows-subgrid gap-y-8"
          style={{ gridRow: `1 / span ${steps.length || 1}` }}
          ref={stepsContainerRef}
        >
          <DragDropProvider onDragEnd={handleDragEndEvent}>
            {steps.map((step, index) => (
              <WorkflowBuilderStep
                key={step._key}
                step={step}
                index={index}
                onRemove={handleRemoveStep}
                onUpdate={handleUpdateStep}
                isEditMode={isEditMode}
                categoryId={categoryId}
                showDueWarning={stepsWithDueWarning.has(index)}
                hideDueDates={hideDueDates}
                hasStepInstance={stepInstances.some(
                  (i) => i.stepId === step.id,
                )}
              />
            ))}
          </DragDropProvider>
        </div>
      </div>

      {isEditMode && (
        <div className="flex items-center justify-center">
          <Button variant="secondary" onClick={handleAddStep} size="sm">
            <Plus className="size-5" />
            {t(($) => $.commons.workflow.addStep)}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-[3rem_1fr] gap-x-6">
        <TimelineEnd />
      </div>
    </div>
  );
}
